import { useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useParams, useNavigate } from 'react-router-dom';
import { Box, Button, Grid, CircularProgress, Typography, Alert, Paper } from '@mui/material';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';

import { toggleFavorite } from '../../actions/users';
import { startChat } from '../../actions/chat';
import { getPublicServiceDetail, getLatestServices } from '../../actions/profile';
import { urlImg, isEmpty } from '../../components/common/helpers';
import { conQuienImparte, nombreDe, fotoDe } from '../../components/common/imparte';
import { useIrAEntrar } from '../../utils/destinoAlEntrar';

import SectionService from '../../components/services/SectionServices';
import ServiceImages from '../../components/services/detail/ServiceImages';
import ServiceHeaderInfo from '../../components/services/detail/ServiceHeaderInfo';
import ServiceReviews from '../../components/services/detail/ServiceReviews';
import ServiceContent from '../../components/services/detail/ServiceContent';
import ServiceLocation from '../../components/services/detail/ServiceLocation';
import NoEncontrado from './NoEncontrado';

const PublicServiceDetail = () => {
  const { userId, serviceId } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  
  const [service, setService] = useState(null);
  const [profile, setProfile] = useState(null);
  const [ownerUser, setOwnerUser] = useState(null);
  // Quién lo imparte (por omisión quien lo creó) y, si es en un grupo o negocio, de qué página
  const [imparten, setImparten] = useState([]);
  const [grupo, setGrupo] = useState(null);
  // Vista previa: el servicio aún no es público (falta que acepte quien lo va a impartir); solo lo ven su dueño y esa persona
  const [vistaPrevia, setVistaPrevia] = useState(null);
  // El servicio existe pero todavía no se puede ver: se explica en vez de mandar al inicio de sesión
  const [noDisponible, setNoDisponible] = useState("");
  // El servicio no existe (o el enlace trae un id inválido): se dice en vez de mandar al inicio
  const [noEncontrado, setNoEncontrado] = useState(false);
  const [latestServices, setLatestServices] = useState([]);

  const authUser = useSelector(state => state.user.user);
  const isOwner = authUser && String(authUser._id) === String(userId);
  const favorites = useSelector(state => state.user.user.favorites || []);
  const isAuthenticated = useSelector(state => state.user.loading === false && state.user.user._id);

  const isFavorite = favorites.some(favId => favId.toString() === serviceId.toString());

  // Sin sesión, el corazón y «Cotizar» mandan a entrar; al entrar regresa a este servicio
  const irAEntrar = useIrAEntrar();

  const onToggleFavorite = () => {
    if (!isAuthenticated) {
      irAEntrar();
      return;
    }
    dispatch(toggleFavorite(serviceId));
  };

  // Los corazones de «Servicios destacados», como en el inicio: llenos en los que ya son favoritos
  const destacadosConFav = latestServices.map(s => ({
    ...s,
    favorite: favorites.some(f => f.toString() === s._id?.toString())
  }));
  const onToggleFavoriteDestacado = (srv) => {
    if (!isAuthenticated) {
      irAEntrar();
      return;
    }
    dispatch(toggleFavorite(srv._id));
  };

  const handleQuote = () => {
    console.log('--- Debug Cotizar (ServiceHeader) ---');
    console.log('isAuthenticated:', isAuthenticated);
    console.log('userId (recipient):', userId);
    console.log('isOwner:', isOwner);

    if (!isAuthenticated) {
      console.warn('Usuario no autenticado');
      irAEntrar();
      return;
    }
    if (userId && !isOwner) {
      console.log('Iniciando chat desde ServiceHeader...');
      const initialMsg = `Hola, me interesa contratar el servicio de "${service.nombre}". Vi tu perfil y me gustaría cotizar. ¿Podrías darme un presupuesto?`;
      dispatch(startChat(userId, serviceId, 'service', initialMsg, `${userId}_${serviceId}`));
    } else {
      console.warn('No se puede iniciar chat: falta userId o es el dueño');
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    const fetchService = async () => {
      try {
        const data = await getPublicServiceDetail(userId, serviceId);
        if (data && data.service) {
          setService(data.service);
          setProfile(data.profile);
          setOwnerUser(data.ownerUser);
          setImparten(data.imparten || []);
          setGrupo(data.grupo || null);
          setVistaPrevia(data.vistaPrevia || null);
          setNoDisponible("");
          setNoEncontrado(false);
        } else if (data && data.noDisponible) {
          setNoDisponible(data.msg);
          setNoEncontrado(false);
        } else if (data && data.noEncontrado) {
          setNoEncontrado(true);
        } else {
          setError(true);
        }
      } catch (err) {
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    const fetchLatestServices = async () => {
      const data = await getLatestServices();
      // La tarjeta muestra a quien imparte el servicio (por omisión, quien lo creó)
      const formattedServices = data.map(conQuienImparte).map(serv => ({
        ...serv,
        authorAvatar: fotoDe({ _id: serv.autorId, avatar: serv.authorAvatar, versionAvatar: serv.authorAvatarVersion }, 'w_150,h_150,c_fill'),
        image: !isEmpty(serv.image) ? `${urlImg}/w_400,h_250,c_fill/servicios/${serv._id}/${serv.image}` : ""
      }));
      setLatestServices(formattedServices.filter(s => s._id !== serviceId));
    };

    fetchService();
    fetchLatestServices();
  }, [userId, serviceId]);

  useEffect(() => {
    if (error) {
      navigate('/', { replace: true });
    }
  }, [error, navigate]);

  if (noEncontrado) {
    return <NoEncontrado />;
  }

  if (noDisponible) {
    return (
      <Box sx={{ minHeight: '60vh', mt: 14, display: 'flex', justifyContent: 'center', alignItems: 'flex-start' }}>
        <Paper elevation={0} variant="outlined" sx={{ p: 4, borderRadius: '12px', textAlign: 'center', maxWidth: 520 }}>
          <Typography variant="h6" fontWeight="bold" color="primary" gutterBottom>
            Este servicio aún no está disponible
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            {noDisponible}
          </Typography>
          <Button variant="contained" onClick={() => navigate('/')} sx={{ textTransform: 'none' }}>
            Ver otros servicios
          </Button>
        </Paper>
      </Box>
    );
  }

  if (loading || error || !service) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', bgcolor: 'grey.50' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Aviso de la vista previa, según quién la mira: quien lo creó o la persona invitada a impartirlo
  const esperado = nombreDe(imparten[0]) || 'la persona elegida';
  const avisoVistaPrevia = !vistaPrevia ? null
    : isOwner
      ? (vistaPrevia.estado === 'rechazado'
        ? `Vista previa: tu servicio no es público. ${esperado} rechazó impartirlo: elige a otra persona o impártelo tú desde tu página.`
        : `Vista previa: tu servicio aún no es público. Se podrá contratar cuando ${esperado} acepte impartirlo.`)
      : (vistaPrevia.estado === 'rechazado'
        ? 'Rechazaste impartir este servicio. No es público.'
        : 'Te invitaron a impartir este servicio. Aún no es público: se podrá contratar cuando aceptes.');

  return (
    <Box sx={{ minHeight: '100vh', pb: 8, pt: 2, mt: 10 }}>
      {/* Back Button */}
      <Button
        startIcon={<ArrowBackIosIcon />}
        onClick={() => navigate(-1)}
        sx={{
          textTransform: 'none',
          color: 'primary.main',
          fontWeight: 'bold',
          mb: 2,
          '&:hover': { bgcolor: 'transparent', opacity: 0.8 }
        }}
      >
        Atrás
      </Button>

      {avisoVistaPrevia && (
        <Alert
          severity={vistaPrevia.estado === 'rechazado' ? 'error' : 'warning'}
          sx={{ mb: 2, borderRadius: '12px' }}
          action={!isOwner && vistaPrevia.estado === 'pendiente' && (
            <Button color="inherit" size="small" onClick={() => navigate('/user/colaboraciones')} sx={{ textTransform: 'none', fontWeight: 700, whiteSpace: 'nowrap' }}>
              Responder invitación
            </Button>
          )}
        >
          {avisoVistaPrevia}
        </Alert>
      )}

      {/* Images Grid */}
      <ServiceImages id={service._id} images={service.img} />

      {/* Header Info (Title, Price, Stats) */}
      <ServiceHeaderInfo 
        service={service} 
        isOwner={isOwner} 
        isFavorite={isFavorite}
        onToggleFavorite={onToggleFavorite}
        onQuote={handleQuote}
        noDisponible={Boolean(vistaPrevia)}
      />

      <Grid container spacing={4}>
        <Grid size={{ xs: 12, md: 5 }}>
          <ServiceLocation service={service} profile={profile} user={ownerUser} />
          <ServiceReviews
            servicioId={service._id}
            ratingPromedio={service.ratingPromedio}
            nombreProveedor={ownerUser ? `${ownerUser.name} ${ownerUser.apellido}` : (profile?.nombre || "Usuario")}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 7 }}>
          <ServiceContent
            service={service}
            profile={profile}
            user={ownerUser}
            imparten={imparten}
            grupo={grupo}
          />
        </Grid>
      </Grid>

      {/* Featured Services Carousel */}
      <Box sx={{ mt: 8 }}>
        <SectionService
          title="Servicios destacados"
          subtitle="Otros servicios que podrían interesarte"
          services={destacadosConFav}
          favoriteService={onToggleFavoriteDestacado}
          irA={() => navigate("/")}
          onVerDetalle={(srv) => navigate(`/servicio/${srv.userId}/${srv._id}`)}
          showPriece
        />
      </Box>
    </Box>
  );
};

export default PublicServiceDetail;
